import {
    EnvironmentProviders,
    importProvidersFrom,
    inject,
    makeEnvironmentProviders,
    provideEnvironmentInitializer,
} from '@angular/core';
import { CookieModule } from '@usana/ux/cookies';
import { URL_SITE_DOMAIN } from '@usana/ux/common/url';
import {
    GRAPH_URL_MARKET,
    I18N_INFO_CONFIG,
    I18nHonorCookie,
    I18nInfoConfig,
    I18nOrganization,
    MarketActiveStatus,
    UsanaMarketsPreloadModule,
} from '@usana/ux/markets';
import { GRAPH_URL_SEO } from '@usana/ux/seo';
import {
    GRAPH_URL_TRANSLATION,
    PackagePrimeMode,
    REST_URL_TRANSLATION,
    TranslationConfig,
} from '@usana/ux/translations';
import { UsanaTranslationsMarketInformedModule } from '@usana/ux/translations/market-informed';
import { environment } from 'src/environments/environment';
import { SharedDotcomConstants } from './dotcom-constants';

/**
 * Returns the environment providers required by the dotcom area. Registered
 * eagerly via AppModule.providers so dotcom-specific tokens live in the root
 * injector. These tokens are required by @usana/ux services that are
 * providedIn: 'root' (e.g. UsanaInternalMarketService, InternalTranslationService,
 * CmsSiteLinkLoaderService), which means their dependencies must be available at
 * root injector scope — not inside the lazy dotcom routes.
 *
 * I18N_INFO_CONFIG is registered here (not in a lazy module) so the root
 * UsanaMarketService instance — created by importProvidersFrom(UsanaMarketsPreloadModule)
 * here — picks it up and pushes the merged config into the singleton
 * UsanaInternalMarketService. Registering it lazily would require a duplicate
 * UsanaMarketsPreloadModule import in the lazy module to instantiate a second
 * UsanaMarketService just to deliver the config.
 *
 * UsanaTranslationsMarketInformedModule and the usanadotcom translation
 * package registration also live here: both mutate root-level singletons
 * (TranslationMarketInformedService and TranslationConfig respectively), so
 * they should fire exactly once at app startup, not once per lazy boundary.
 */
export function provideDotcomEnvironment(): EnvironmentProviders {
    return makeEnvironmentProviders([
        importProvidersFrom(CookieModule, UsanaMarketsPreloadModule, UsanaTranslationsMarketInformedModule),
        { provide: URL_SITE_DOMAIN, useValue: environment.SITE_DOMAIN },
        { provide: GRAPH_URL_MARKET, useValue: `${environment.I18N_URL}/graphql` },
        { provide: GRAPH_URL_TRANSLATION, useValue: `${environment.I18N_URL}/graphql` },
        { provide: REST_URL_TRANSLATION, useValue: environment.I18N_URL },
        { provide: GRAPH_URL_SEO, useValue: `${environment.I18N_URL}/graphql` },
        {
            provide: I18N_INFO_CONFIG,
            useValue: new I18nInfoConfig(
                false,
                false,
                true,
                false,
                true,
                false,
                false,
                false,
                MarketActiveStatus.ACTIVE,
                I18nOrganization.USANA,
                I18nHonorCookie.ALWAYS,
                true,
                true,
            ),
            multi: true,
        },
        provideEnvironmentInitializer(() => {
            inject(TranslationConfig).addPackage({
                pkg: SharedDotcomConstants.TRANSLATION_NAMESPACE,
                primeMode: PackagePrimeMode.ON_INIT,
                showTagWhenMissing: false,
            });
        }),
    ]);
}
